import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

// 11 photos with editorial rhythm: variable heights (vh) and trailing gaps (px)
// All images are part of the "A Piece of Sky" EP visual series — Berlin, 2025.
const EP_META = { meta: 'A Piece of Sky', date: 'EP 001 · BERLIN · 2025' };

// Credits — visual team behind the A Piece of Sky shoot.
const CREDITS = [
  { handle: 'laetishirt',          role: { fr: 'Direction artistique',     en: 'Creative Director',          de: 'Kreativdirektion' } },
  { handle: 'rhyspll',             role: { fr: 'Photographe',              en: 'Photographer',               de: 'Fotograf' } },
  { handle: 'emotionalriots',      role: { fr: 'Studio & assistance lumière', en: 'Studio & Light Assistant', de: 'Studio & Lichtassistenz' } },
  { handle: 'sbizien',             role: { fr: 'Hairstyliste',             en: 'Hairstylist',                de: 'Hairstylist' } },
  { handle: 'kipras_mash',         role: { fr: 'Assistant hairstyle',      en: 'Hairstylist Assistant',      de: 'Hairstyle-Assistenz' } },
  { handle: 'lucianprietosanchez', role: { fr: 'Styliste',                 en: 'Stylist',                    de: 'Stylist' } },
  { handle: 'alv_mua',             role: { fr: 'Maquillage',               en: 'Make-Up Artist',             de: 'Make-Up' } },
  { handle: 'afrobodega',          role: { fr: 'Marque',                   en: 'Brand',                      de: 'Marke' } },
  { handle: 'sangiev',             role: { fr: 'Marque',                   en: 'Brand',                      de: 'Marke' } },
];
const IMAGES = [
  { src: '/TSANGA 1 exp.jpg',   ...EP_META, h: 72, gap: 32  },
  { src: '/TSANGA 2 exp.jpg',   ...EP_META, h: 48, gap: 80  },
  { src: '/TSANGA 3 exp.jpg',   ...EP_META, h: 62, gap: 48  },
  { src: '/TSANGA 4 exp.jpg',   ...EP_META, h: 38, gap: 120 },
  { src: '/TSANGA 5  exp.jpg',  ...EP_META, h: 70, gap: 40  },
  { src: '/TSANGA 6 exp.jpg',   ...EP_META, h: 54, gap: 64  },
  { src: '/TSANGA 7 exp.jpg',   ...EP_META, h: 44, gap: 96  },
  { src: '/tsanga 9 exp.jpg',   ...EP_META, h: 66, gap: 48  },
  { src: '/TSANGA 10  exp.jpg', ...EP_META, h: 50, gap: 80  },
  { src: '/TSANGA 11 exp.jpg',  ...EP_META, h: 72, gap: 56  },
  { src: '/TSANGA 12 exp.jpg',  ...EP_META, h: 58, gap: 120 },
];

// Approx card aspect ratio so we can compute total track width before render
// (portrait ~0.7, landscape ~1.5 — average ≈ 1.1 of card height)
const aspectFor = (h) => (h >= 60 ? 0.72 : 1.42); // portrait if tall, landscape if shorter

export default function Gallery() {
  const { lang } = useLang();
  const t = T[lang]?.gallery || T.fr.gallery;

  const sectionRef = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index | null

  const openLightbox = useCallback((i) => setLightbox(i), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prevLightbox = useCallback(
    () => setLightbox((i) => (i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length)),
    []
  );
  const nextLightbox = useCallback(
    () => setLightbox((i) => (i === null ? null : (i + 1) % IMAGES.length)),
    []
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') prevLightbox();
      else if (e.key === 'ArrowRight') nextLightbox();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, closeLightbox, prevLightbox, nextLightbox]);

  useEffect(() => {
    const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqMobile = window.matchMedia('(max-width: 768px)');
    const update = () => {
      setReduced(mqReduced.matches);
      setIsMobile(mqMobile.matches);
    };
    update();
    mqReduced.addEventListener('change', update);
    mqMobile.addEventListener('change', update);
    return () => {
      mqReduced.removeEventListener('change', update);
      mqMobile.removeEventListener('change', update);
    };
  }, []);

  // Scroll progress through the section (0 → 1 across its full vertical extent)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 80, damping: 22, mass: 0.4 });

  // Total horizontal track width vs viewport — compute in vh + px gaps + side padding
  // Use 1vh ≈ window.innerHeight/100 at runtime; for SSR-safety use 8 (approx 800px viewport / 100)
  const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight / 100 : 8);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => {
      setVh(window.innerHeight / 100);
      setVw(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const SIDE_PAD = 56;
  const CREDITS_W = 520; // width of the trailing credits panel (last image carries the gap)
  const trackPx = IMAGES.reduce(
    (sum, img) => sum + img.h * vh * aspectFor(img.h) + img.gap,
    0
  ) + CREDITS_W;
  const maxTranslate = Math.max(0, trackPx + SIDE_PAD * 2 - vw);

  // Section height: ~1 viewport of slack + the horizontal travel distance, mapped to scroll
  const sectionHeightPx = vw && vh ? maxTranslate + vh * 100 : 5000;

  const x = useTransform(smoothProgress, [0, 1], [0, -maxTranslate]);
  const hintOpacity = useTransform(smoothProgress, [0, 0.1], [1, 0]);

  // Compute current dominant photo for the sticky meta panel
  const [currentIdx, setCurrentIdx] = useState(0);
  useEffect(() => {
    return smoothProgress.on('change', (p) => {
      // Find which photo center is closest to viewport center (vw/2)
      const targetX = p * maxTranslate + vw / 2 - SIDE_PAD;
      let acc = 0;
      let idx = 0;
      for (let i = 0; i < IMAGES.length; i++) {
        const w = IMAGES[i].h * vh * aspectFor(IMAGES[i].h);
        const center = acc + w / 2;
        if (center >= targetX) { idx = i; break; }
        acc += w + IMAGES[i].gap;
        idx = i;
      }
      setCurrentIdx(idx);
    });
  }, [smoothProgress, maxTranslate, vw, vh]);

  // ---------------- MOBILE FALLBACK ----------------
  if (isMobile || reduced) {
    return (
      <section
        id="gallery"
        style={{
          position: 'relative', background: 'var(--bg)',
          padding: '64px 20px 80px', display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <div className="noise" aria-hidden="true" />
        <ChapterBar t={t} />
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 28, marginTop: 24,
        }}>
          {IMAGES.map((img, i) => (
            <figure key={img.src} style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => openLightbox(i)}
                aria-label={`Voir photo ${i + 1} en plein écran — ${img.meta}`}
                style={{
                  position: 'relative', width: '100%', overflow: 'hidden',
                  padding: 0, border: 0, background: 'transparent', cursor: 'pointer',
                  display: 'block',
                }}
              >
                <img
                  src={img.src}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  style={{
                    width: '100%', height: 'auto', display: 'block',
                    objectPosition: 'center 22%',
                    filter: 'contrast(1.05) saturate(0.9)',
                  }}
                />
              </button>
              <figcaption style={{
                display: 'flex', justifyContent: 'space-between',
                fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.2em',
                textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
              }}>
                <span>N° {String(i + 1).padStart(2, '0')} / 11</span>
                <span>{img.meta} · {img.date}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <CreditsPanel lang={lang} t={t} width={520} variant="mobile" />
        <Lightbox
          index={lightbox}
          onClose={closeLightbox}
          onPrev={prevLightbox}
          onNext={nextLightbox}
        />
      </section>
    );
  }

  // ---------------- DESKTOP: HORIZONTAL SCROLL ----------------
  return (
    <section
      ref={sectionRef}
      id="gallery"
      style={{
        position: 'relative',
        height: `${sectionHeightPx}px`,
        background: 'var(--bg)',
      }}
      aria-label="Gallery"
    >
      <div className="noise" aria-hidden="true" />

      {/* Sticky viewport — content stays as we scroll through the section's vertical extent */}
      <div style={{
        position: 'sticky', top: 0, height: '100vh', width: '100%',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Chapter bar */}
        <div style={{
          padding: '80px 56px 0', flexShrink: 0,
        }}>
          <ChapterBar t={t} />
        </div>

        {/* Sticky meta panel — left overlay */}
        <motion.div
          aria-hidden="true"
          style={{
            position: 'absolute', left: 56, bottom: 100, zIndex: 5,
            display: 'flex', flexDirection: 'column', gap: 10,
            pointerEvents: 'none',
          }}
        >
          <motion.div
            key={`num-${currentIdx}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            style={{
              fontFamily: 'var(--display)', fontSize: 72, lineHeight: 0.9,
              fontWeight: 300, color: '#fff', letterSpacing: '-0.02em',
            }}
          >
            {String(currentIdx + 1).padStart(2, '0')}
            <span style={{
              fontSize: 22, color: 'rgba(255,255,255,0.35)', marginLeft: 8,
            }}>/ 11</span>
          </motion.div>
          <motion.div
            key={`meta-${currentIdx}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE, delay: 0.05 }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)',
            }}
          >
            {IMAGES[currentIdx].meta}
          </motion.div>
          <motion.div
            key={`date-${currentIdx}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE, delay: 0.1 }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
            }}
          >
            {IMAGES[currentIdx].date}
          </motion.div>
        </motion.div>

        {/* Horizontal track */}
        <div style={{
          flex: 1, position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center',
        }}>
          <motion.div
            style={{
              x,
              display: 'flex', alignItems: 'center',
              paddingLeft: SIDE_PAD, paddingRight: SIDE_PAD,
              willChange: 'transform',
            }}
          >
            {IMAGES.map((img, i) => (
              <GalleryCard
                key={img.src}
                img={img}
                index={i}
                progress={smoothProgress}
                vh={vh}
                vw={vw}
                maxTranslate={maxTranslate}
                onOpen={() => openLightbox(i)}
                trackOffset={IMAGES.slice(0, i).reduce(
                  (s, p) => s + p.h * vh * aspectFor(p.h) + p.gap,
                  0
                )}
              />
            ))}
            <CreditsPanel lang={lang} t={t} width={CREDITS_W} />
          </motion.div>
        </div>

        {/* Progress bar — bottom */}
        <div style={{
          position: 'absolute', left: 56, right: 56, bottom: 40, zIndex: 5,
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div style={{
            flex: 1, height: 1, background: 'rgba(255,255,255,0.12)',
            position: 'relative', overflow: 'hidden',
          }}>
            <motion.div
              style={{
                position: 'absolute', inset: 0, originX: 0,
                background: 'rgba(255,255,255,0.85)',
                scaleX: smoothProgress,
              }}
            />
          </div>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.22em',
            color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase',
            minWidth: 70, textAlign: 'right',
          }}>
            {String(currentIdx + 1).padStart(2, '0')} / 11
          </span>
        </div>

        {/* Scroll hint — top right, fades as user scrolls in */}
        <motion.div
          aria-hidden="true"
          style={{
            position: 'absolute', top: 92, right: 56, zIndex: 5,
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.24em',
            color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
            opacity: hintOpacity,
          }}
        >
          {t.scrollHint}
        </motion.div>
      </div>

      <Lightbox
        index={lightbox}
        onClose={closeLightbox}
        onPrev={prevLightbox}
        onNext={nextLightbox}
      />
    </section>
  );
}

function CreditsPanel({ lang, t, width, variant = 'desktop' }) {
  const isMobile = variant === 'mobile';
  return (
    <motion.aside
      initial={{ opacity: 0, y: isMobile ? 12 : 0 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: EASE }}
      style={{
        flexShrink: 0,
        width: isMobile ? '100%' : width,
        maxWidth: isMobile ? 520 : width,
        margin: isMobile ? '32px auto 0' : 0,
        marginRight: isMobile ? 'auto' : 0,
        padding: isMobile ? '28px 0 0' : '24px 0',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', flexDirection: 'column', gap: 16,
        alignSelf: 'center',
      }}
      aria-label={t.creditsLabel || 'Credits'}
    >
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12,
        fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
      }}>
        <span style={{ color: 'rgba(255,255,255,0.85)' }}>{t.creditsLabel || 'Credits'}</span>
        <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        <span>A PIECE OF SKY</span>
      </div>

      <ul style={{
        listStyle: 'none', margin: 0, padding: 0,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {CREDITS.map(({ handle, role }) => (
          <li
            key={handle}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(140px, max-content) 1fr',
              gap: 16, alignItems: 'baseline',
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
              textTransform: 'uppercase',
            }}
          >
            <a
              href={`https://instagram.com/${handle}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.18)',
                paddingBottom: 2,
                transition: 'color 0.2s, border-color 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
              }}
            >
              @{handle}
            </a>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>
              {role[lang] || role.en}
            </span>
          </li>
        ))}
      </ul>
    </motion.aside>
  );
}

function Lightbox({ index, onClose, onPrev, onNext }) {
  if (typeof document === 'undefined') return null;
  const img = index !== null ? IMAGES[index] : null;

  return createPortal(
    <AnimatePresence>
      {img && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${index + 1} sur 11 — ${img.meta}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: EASE }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(2, 2, 6, 0.96)',
            backdropFilter: 'blur(8px)',
            display: 'grid', placeItems: 'center',
            cursor: 'zoom-out',
          }}
        >
          {/* 1:1 square frame, capped by viewport */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            style={{
              position: 'relative',
              width: 'min(86vh, 86vw)',
              height: 'min(86vh, 86vw)',
              cursor: 'zoom-out',
              boxShadow: '0 40px 120px rgba(0,0,0,0.6)',
              overflow: 'hidden',
            }}
          >
            <motion.img
              key={img.src}
              src={img.src}
              alt=""
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{
                width: '100%', height: '100%',
                objectFit: 'contain', // full image inside 1:1 frame
                display: 'block',
                filter: 'contrast(1.05) saturate(0.95)',
              }}
            />
          </motion.div>

          {/* Top bar: counter + close */}
          <div style={{
            position: 'fixed', top: 24, left: 0, right: 0,
            display: 'flex', justifyContent: 'space-between',
            padding: '0 28px',
            fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)',
            pointerEvents: 'none',
          }}>
            <span>N° {String(index + 1).padStart(2, '0')} / 11</span>
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label="Fermer (Échap)"
              style={{
                pointerEvents: 'auto',
                width: 36, height: 36,
                display: 'grid', placeItems: 'center',
                background: 'transparent', border: 0,
                color: 'rgba(255,255,255,0.65)',
                fontSize: 16, lineHeight: 1, cursor: 'pointer',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
            >
              ✕
            </button>
          </div>

          {/* Bottom bar: meta + arrows */}
          <div style={{
            position: 'fixed', bottom: 28, left: 0, right: 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0 28px',
            fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)',
          }}>
            <span style={{ pointerEvents: 'none' }}>{img.meta} · {img.date}</span>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { label: '←', fn: onPrev, aria: 'Photo précédente' },
                { label: '→', fn: onNext, aria: 'Photo suivante' },
              ].map(({ label, fn, aria }) => (
                <button
                  key={label}
                  onClick={(e) => { e.stopPropagation(); fn(); }}
                  aria-label={aria}
                  style={{
                    width: 44, height: 44,
                    display: 'grid', placeItems: 'center',
                    border: '1px solid rgba(255,255,255,0.25)',
                    background: 'rgba(4,4,10,0.5)',
                    color: '#fff', fontSize: 18,
                    fontFamily: 'var(--mono)', cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function ChapterBar({ t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: EASE }}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        paddingBottom: 18, borderBottom: '1px solid var(--line)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <span style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 300, color: '#fff' }}>II.</span>
        <span style={{
          fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)',
        }}>{t.title}</span>
      </div>
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.18em',
        color: 'var(--faint)', textTransform: 'uppercase',
      }}>{t.meta}</span>
    </motion.div>
  );
}

function GalleryCard({ img, index, progress, vh, vw, maxTranslate, trackOffset, onOpen }) {
  const [hovered, setHovered] = useState(false);

  const w = img.h * vh * aspectFor(img.h);
  const cardH = img.h * vh;

  // Card progress: 0 when card is fully off-screen right, 1 when off-screen left
  // The card's left edge at scroll progress p is at: (-p * maxTranslate) + trackOffset + SIDE_PAD
  // Card is centered in viewport when that equals (vw - w) / 2
  const cardProgress = useTransform(progress, (p) => {
    const cardLeft = -p * maxTranslate + trackOffset + 56;
    const cardCenter = cardLeft + w / 2;
    // -1 = card center off right, 0 = centered, 1 = off left
    return (vw / 2 - cardCenter) / (vw / 2 + w / 2);
  });

  // Subtle parallax: translateY based on how far through viewport the card is
  const parallaxY = useTransform(cardProgress, [-1, 0, 1], [10, 0, -10]);
  // Fade in from edges
  const cardOpacity = useTransform(cardProgress, [-1.1, -0.85, 0.85, 1.1], [0, 1, 1, 0]);

  return (
    <motion.figure
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(); } }}
      role="button"
      tabIndex={0}
      aria-label={`Voir photo ${index + 1} en plein écran — ${img.meta}`}
      style={{
        flexShrink: 0, margin: 0, marginRight: img.gap,
        width: w, height: cardH,
        position: 'relative', overflow: 'hidden',
        opacity: cardOpacity,
        cursor: 'zoom-in',
        // Stagger vertical position by index for editorial rhythm
        marginTop: (index % 3 === 0 ? -24 : index % 3 === 1 ? 12 : -8),
      }}
    >
      <motion.img
        src={img.src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        animate={{
          scale: hovered ? 1.04 : 1,
          filter: hovered
            ? 'grayscale(0) brightness(1) contrast(1.05) saturate(0.95)'
            : 'grayscale(0.5) brightness(0.82) contrast(1.05) saturate(0.85)',
        }}
        transition={{ duration: 0.5, ease: EASE }}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: 'center 22%', display: 'block',
          y: parallaxY,
          willChange: 'transform, filter',
        }}
      />

      {/* Hover caption */}
      <motion.figcaption
        animate={{
          opacity: hovered ? 1 : 0,
          y: hovered ? 0 : 6,
        }}
        transition={{ duration: 0.3, ease: EASE }}
        style={{
          position: 'absolute', left: 14, bottom: 14, right: 14,
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#fff',
          display: 'flex', justifyContent: 'space-between', gap: 12,
          textShadow: '0 1px 8px rgba(0,0,0,0.6)',
          pointerEvents: 'none',
        }}
      >
        <span>N° {String(index + 1).padStart(2, '0')}</span>
        <span style={{ textAlign: 'right' }}>{img.meta}<br />{img.date}</span>
      </motion.figcaption>
    </motion.figure>
  );
}
